import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { phoneDisplay, phoneHref, waLink } from "@/data/products";

const EASE = [0.2, 0.7, 0.2, 1];

export default function Hero() {
  const rm = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  const reveal = (delay) => ({
    initial: rm ? false : { y: "112%" },
    animate: { y: 0 },
    transition: { duration: 0.75, delay, ease: EASE },
  });

  return (
    <header className="hero" ref={ref} data-testid="hero-section">
      <motion.div
        className="hero-top"
        initial={rm ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: EASE }}
      >
        <span className="mono-tag">AHUJA BROTHERS<sup>®</sup> — IRON &amp; STEEL · <span className="since-tag" data-testid="since-tag">SINCE 1967</span></span>
        <a className="mono-tag hero-phone" href={phoneHref()} data-testid="hero-phone-link">
          {phoneDisplay()}
        </a>
      </motion.div>

      <motion.div className="hero-main" style={{ y }}>
        <div className="hero-type">
          <span className="mask-line">
            <motion.h1 className="hero-line" {...reveal(0.15)}>
              AHUJA BROTHERS<sup className="reg-mark">®</sup>
            </motion.h1>
          </span>
          <span className="mask-line mask-steel">
            <motion.h1 className="hero-line hero-line-steel" {...reveal(0.28)}>
              <svg
                className="steel-svg"
                viewBox="0 0 618 78"
                role="img"
                aria-label="Iron &amp; Steel Traders"
                data-testid="steel-texture-svg"
              >
                <defs>
                  <filter id="millScale" x="-5%" y="-5%" width="110%" height="110%">
                    <feTurbulence
                      type="fractalNoise"
                      baseFrequency="0.015 0.04"
                      numOctaves="3"
                      seed="7"
                      result="n"
                    >
                      <animate
                        attributeName="baseFrequency"
                        dur="20s"
                        repeatCount="indefinite"
                        calcMode="spline"
                        values="0.015 0.04; 0.018 0.048; 0.015 0.04"
                        keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
                      />
                    </feTurbulence>
                    <feComponentTransfer in="n" result="c">
                      <feFuncR type="table" tableValues="0.3 0.58" />
                      <feFuncG type="table" tableValues="0.31 0.58" />
                      <feFuncB type="table" tableValues="0.33 0.62" />
                    </feComponentTransfer>
                    <feComposite in="c" in2="SourceAlpha" operator="in" />
                  </filter>
                </defs>
                <text className="steel-text-base" x="0" y="72" textLength="604" lengthAdjust="spacingAndGlyphs">
                  IRON &amp; STEEL TRADERS
                </text>
                <text
                  className="steel-text-tex"
                  x="0"
                  y="72"
                  textLength="604"
                  lengthAdjust="spacingAndGlyphs"
                  filter="url(#millScale)"
                >
                  IRON &amp; STEEL TRADERS
                </text>
              </svg>
            </motion.h1>
          </span>
          <span className="mask-line mask-small">
            <motion.h1 className="hero-line hero-line-small" {...reveal(0.41)}>
              LOHA MANDI, NARAINA, NEW DELHI
            </motion.h1>
          </span>
        </div>

        <motion.p
          className="hero-sub"
          data-testid="hero-subline"
          initial={rm ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.85, ease: EASE }}
        >
          Angles, channels, sheets, plates, TMT, flats, pipes. Cut to your size.
          Ask for today&apos;s rate.
        </motion.p>

        <motion.div
          className="hero-cta"
          initial={rm ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0, ease: EASE }}
        >
          <motion.a
            className="btn-furnace"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            whileHover={rm ? undefined : { scale: 1.03 }}
            whileTap={rm ? undefined : { scale: 0.97 }}
          >
            WHATSAPP FOR RATE
          </motion.a>
          <motion.a
            className="btn-furnace"
            href={phoneHref()}
            data-testid="hero-call-button"
            whileHover={rm ? undefined : { scale: 1.03 }}
            whileTap={rm ? undefined : { scale: 0.97 }}
          >
            CALL NOW
          </motion.a>
        </motion.div>
      </motion.div>

      <motion.div
        className="hero-foot mono-tag"
        initial={rm ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.2, ease: EASE }}
      >
        <span>Y-257 · LOHA MANDI · NARAINA · NEW DELHI – 110028</span>
        <span className="hero-foot-right">DWG. NO. AB-01 · REV. TODAY</span>
      </motion.div>
    </header>
  );
}
